import React from 'react';
import { Text } from '@mantine/core';

/**
 * The per-goal rows shown beneath a variation when an "All <type>" option is
 * selected.
 *
 * These are real <tr> rows in the MAIN table, not a nested table. That is what
 * makes every figure sit under the heading it belongs to: a nested table lays
 * itself out independently and can never line up with the table around it.
 *
 * The caller passes `columns` — the main table's column layout, in order — so the
 * breakdown emits exactly one cell per column and inherits the parent's widths.
 * Columns the breakdown has nothing to say about (ID, P Status, Choose Winner,
 * Pause Variation) render empty rather than being skipped, which is what keeps the
 * alignment exact.
 *
 * Column labels come straight from `columns`, so they always read the same as the
 * main table's headings.
 *
 * Uniques and Visits belong to the variation, not to any one goal, so those cells
 * are left blank — the figures already sit on the variation row above.
 *
 * Every other value is printed exactly as the Single Conversion (single-goal) row
 * prints it: same markup, same wording, no extra formatting. A zero reads as a
 * zero, "Not Enough Data" reads in red, and an absent significance leaves the cell
 * empty — so an expanded goal row is indistinguishable in style from the row above.
 */

/** Columns the breakdown puts a value in; every other column renders empty. */
const FILLED = new Set([
  'variations', 'conv_num', 'conv_pct',
  'pi', 'ss', 'sub_rev', 'revenue', 'aov', 'epc',
]);

export function GoalBreakdownRows({
  goals,
  columns,
  isDark = false,
  rowKeyPrefix = '',
  headingLabel = 'Goal',
  blankColumns = [],
  /* The repeated column-header row. The goal breakdown wants it - an expanded block far down
     the page needs its own headings - but the Customer Type block sits directly beneath the
     variation row it belongs to, so a second header line there is just noise. */
  showHeading = true,
  onConvNumClick = null,
  // Left offsets for the sticky identity columns, or null when sticky is off.
  // Sub-rows must stick too or they slide under the pinned cells on scroll.
  stickyLefts = null,
}: any) {
  if (!Array.isArray(goals) || goals.length === 0 || !Array.isArray(columns) || columns.length === 0) {
    return null;
  }

  const c = {
    band:     isDark ? '#1c1d21' : '#fafbfc',
    headInk:  isDark ? '#909296' : '#7c8791',
    ink:      isDark ? '#e9ecef' : '#212529',
    line:     isDark ? '#2c2e33' : '#e9ecef',
    accent:   isDark ? '#4a4d55' : '#ced4da',
  };

  /* No font-size override: the cells inherit the table's own size, so a goal row
     reads identically to the variation row above it. */
  const tdBase: React.CSSProperties = {
    background: c.band,
    color: isDark ? '#ffffff' : undefined,
  };
  const headTd: React.CSSProperties = {
    ...tdBase,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    color: c.headInk,
    whiteSpace: 'nowrap',
    borderTop: `1px solid ${c.line}`,
  };

  /* The label column: the goal name, nothing else. The row's state already reads
     from the Percentage Improvement column, exactly as it does for a single goal. */
  const goalNameCell = (goal: any) => (
    <span style={{ fontWeight: 600, color: isDark ? '#ffffff' : c.ink }} title={goal?.goal_name}>
      {goal?.goal_name || '-'}
    </span>
  );

  /* Mirrors the main row's improvement cell markup, so the bars line up with the
     ones above them. */
  const piCell = (goal: any) => {
    const piPer = goal?.pi_per;
    /* "Not Enough Data" prints grey, exactly as the variation row does - it is a
       message, not a missing value. */
    if (piPer === 'Not Enough Data') {
      return (
        <Text
          fw={500}
          style={{ color: isDark && goal?.column_heighlite === 'red' ? '#d0d0d0' : undefined }}
          c={isDark && goal?.column_heighlite === 'red' ? undefined : 'dimmed'}
        >
          {piPer}
        </Text>
      );
    }
    if (piPer === '-' || piPer === undefined || piPer === null || piPer === '') {
      return null;
    }
    return (
      <div style={{ display: 'flex' }}>
        <Text fw={500} w={40}>{Math.round(piPer)}%</Text>
        <div className="pi_graph pi_cf">
          <div className={`pi_ext ${goal?.pi_class || ''}`}>
            <div
              style={{ width: `${Math.min(Math.round(goal?.pi_per_value || 0), 100)}%` }}
              className={`pi_bar ${goal?.pi_class || ''}`}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  /* Same shape as the main row's significance cell. */
  const ssCell = (goal: any) => {
    /* Same gate and same markup as the variation row; an empty cell when there is
       nothing to show, not a dash. ss_class is normalised to '' - the backend's own
       default - so a missing value cannot slip past the gate as "0%". */
    const ssClass = goal?.ss_class ?? '';
    const ssPer = goal?.ss_per;
    if (!(ssClass !== '' || ssPer !== 0)) return null;
    return (
      <div style={{ display: 'flex' }}>
        <Text fw={500} style={{ width: '5.5rem' }}>{ssPer}%</Text>
        {ssPer !== ''
          ? <progress value={ssPer} max="100" className={ssClass}
                      style={{ width: '208%', marginTop: '3px' }}></progress>
          : ''}
      </div>
    );
  };

  const isBlank = (key: string) => blankColumns.indexOf(key) !== -1;

  const valueFor = (key: string, goal: any) => {
    if (isBlank(key)) return null;
    switch (key) {
      case 'variations': return goalNameCell(goal);
      case 'conv_num':   return goal?.pageconvnum;
      case 'conv_pct':   return <>{goal?.pageconv} %</>;
      case 'pi':         return piCell(goal);
      case 'ss':         return ssCell(goal);
      case 'sub_rev':    return goal?.sub_revenue;
      case 'revenue':    return goal?.revenue;
      case 'aov':        return goal?.aov;
      case 'epc':        return goal?.avg_revenue_per_unique;
      /* Uniques, Visits, ID, P Status, Choose Winner, Avg. Time Spent, Pause… */
      default:           return null;
    }
  };

  return (
    <>
      {/* Headings, repeated so an expanded block far down the page still reads.
          Wording comes from the main table's own column labels. */}
      {showHeading && (
      <tr key={`${rowKeyPrefix}goal-head`}>
        {columns.map((col: any, i: number) => (
          <td
            key={col.key}
            style={{
              ...headTd,
              ...(i === 0 ? { borderLeft: `3px solid ${c.accent}` } : null),
              ...(stickyLefts && i < 3
                ? { position: 'sticky' as const, left: stickyLefts[i], zIndex: 2 }
                : null),
            }}
            className={stickyLefts && i < 3 ? 'vt-sticky' : undefined}
          >
            {FILLED.has(col.key) && !isBlank(col.key)
              ? (col.key === 'variations' ? headingLabel : col.label)
              : ''}
          </td>
        ))}
      </tr>
      )}

      {goals.map((goal: any, goalIndex: number) => (
        <tr key={`${rowKeyPrefix}goal-${goal?.goal_id ?? goalIndex}`}>
          {columns.map((col: any, i: number) => {
            /* Conversion count opens the Customer Journey when the caller wires it up. */
            const isConvCell = col.key === 'conv_num' && onConvNumClick && Number(goal?.pageconvnum) > 0;
            return (
              <td
                key={col.key}
                className={stickyLefts && i < 3 ? 'vt-sticky' : undefined}
                onClick={isConvCell ? () => onConvNumClick(goal) : undefined}
                style={{
                  ...tdBase,
                  ...(i === 0 ? { borderLeft: `3px solid ${c.accent}` } : null),
                  ...(stickyLefts && i < 3
                    ? { position: 'sticky' as const, left: stickyLefts[i], zIndex: 2 }
                    : null),
                  ...(isConvCell ? { cursor: 'pointer' } : null),
                }}
              >
                {valueFor(col.key, goal)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
